import {
  DropDownList,
  type DropDownListChangeEvent,
  type DropDownListHandle,
} from "@progress/kendo-react-dropdowns";

import { ICON_BASE_PATH } from "../../../../utils/config";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import React from "react";

interface RoleOption {
  value: number;
  text: string;
}

interface DropdownInputProps {
  id?: string;
  value?: string | null;
  onChange: (val: { value: string }) => void;
  onValidate?: (isValid: boolean) => void;
  label?: string;
  data: RoleOption[] | null | [];
  required?: boolean;
  validationMessage?: string;
  className?: string;
  placeholder?: string;
  onClick?: () => void;
  iconSrc?: string;
  disable?: boolean;
  triggerValidation?: boolean;
}

const FinType: React.FC<DropdownInputProps> = ({
  id = "select-dropdown",
  value,
  onChange,
  onValidate,
  label,
  data = [],
  required = false,
  className = "",
  placeholder = "Type",
  disable = false,
  triggerValidation,
}) => {
  const { t } = useTranslation();
  const options: RoleOption[] = Array.isArray(data) ? [...data] : [];
  const dropDownRef = useRef<DropDownListHandle>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [touched, setTouched] = useState(false);
  const [localValidationMessage, setLocalValidationMessage] = useState("");
  const selectedItem: RoleOption | null =
    options.find(
      (opt) => String(opt.value) === String(value) || opt.text === value,
    ) ?? null;
  const showFloatingLabel = isFocused || !!value;

  const fieldName = label || placeholder || "option";

  const validateSelection = (val: string | null | undefined): boolean => {
    if (required && (!val || val === "" || val === placeholder)) {
      setLocalValidationMessage(`Please select a ${fieldName}.`);
      return false;
    }

    setLocalValidationMessage("");

    return true;
  };

  const updateValidation = (val: string | null | undefined) => {
    const valid = validateSelection(val);
    setIsValid(valid);
    onValidate?.(valid);
  };

  const handleChange = (e: DropDownListChangeEvent) => {
    const selected = e.target.value as RoleOption | null;

    if (selected) {
      onChange({ value: String(selected.value) });
    }

    setTouched(true);

    updateValidation(selected?.text);
  };

  const handleBlur = () => {
    setTouched(true);
    setIsFocused(false);

    updateValidation(value);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  useEffect(() => {
    if (triggerValidation) {
      setTouched(true);
      updateValidation(value);
    }
  }, [triggerValidation, value]);

  const handleIconClick = () => {
    if (!disable) {
      dropDownRef.current?.togglePopup();
      dropDownRef.current?.focus();
    }
  };

  const handleLabelClick = () => {
    if (!disable) {
      dropDownRef.current?.focus();
    }
  };

  return (
    <div className="relative gx-dropdown securityquestions">
      <div
        className={`datepicker-field-wrapper ${className} ${
          !isValid && showFloatingLabel ? "invalid" : ""
        }`}
      >
        <label
          id={`${id}-label`}
          className={`input-label gx-font-semibold ${value ? "floating" : ""}`}
          onClick={handleLabelClick}
        >
          {t(placeholder)}

          {required && (
            <span
              className={`${
                value ? "text-[0.625rem]" : "text-lg"
              } text-red-500 ms-1`}
            >
              *
            </span>
          )}
        </label>

        <div
          className={`gx-dropdown-container ${
            !isValid && touched ? "invalid" : ""
          }`}
          aria-invalid={!isValid}
        >
          <DropDownList
            id={id}
            ref={dropDownRef}
            data={options}
            value={selectedItem}
            textField="text"
            dataItemKey="value"
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            filterable={false}
            disabled={disable}
            ariaLabelledBy={`${id}-label`}
            className={selectedItem ? "gx-filled" : "gx-placeholder"}
            itemRender={(li, itemProps) => {
              const itemText = itemProps.dataItem?.text ?? "";

              const isPlaceholder = itemText === placeholder;

              return React.cloneElement(li as React.ReactElement<any>, {
                className: `text-sm px-4 py-2 hover:bg-gray-100 ${
                  isPlaceholder ? "text-gray-400" : "text-gray-700"
                }`,
                children: itemText,
              });
            }}
          />

          <img
            src={`${ICON_BASE_PATH}down_arrow1.svg`}
            alt=""
            aria-hidden="true"
            className="gx-dropdown-icon"
            onClick={handleIconClick}
          />
        </div>

        {touched && localValidationMessage && (
          <div
            className="gx_input_validationmessage gx-font-regular"
            role="alert"
          >
            {localValidationMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default FinType;
